import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import {
  Mail, MessageCircle, Twitter, Building2, TrendingUp, Newspaper, Bug, LifeBuoy,
  Handshake, BarChart3, ArrowRight, Clock, ShieldCheck,
} from 'lucide-react';

// Contact-channel cards. Categorized so visitors know exactly where their
// message goes — generic "contact us" forms have lower response rates because
// users (especially partners + investors) want to feel they're reaching the
// right inbox, not the general queue.
const CHANNELS = [
  {
    key: 'support',
    icon: LifeBuoy,
    accent: 'amber',
    title: 'Customer support',
    body: 'Account access, billing, bot setup, exchange API key questions. Aim for a response within 1 business day.',
    email: 'support@haythix.com',
    eyebrow: 'Existing users',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        Self-serve first: most setup questions are answered in our{' '}
        <Link to="/help-center" className="text-amber-400 hover:underline">Help Center</Link>{' '}
        and{' '}
        <Link to="/guides" className="text-amber-400 hover:underline">Learning Library</Link>.
      </p>
    ),
  },
  {
    key: 'partnerships',
    icon: Handshake,
    accent: 'emerald',
    title: 'Partnerships',
    body: 'Co-marketing, exchange integrations, protocol partnerships, distribution deals, joint research, white-label discussions.',
    email: 'partnerships@haythix.com',
    eyebrow: 'Companies + protocols',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        Include your team, what you build, and what kind of collaboration you have in mind. The shorter and more specific the better.
      </p>
    ),
  },
  {
    key: 'investors',
    icon: TrendingUp,
    accent: 'violet',
    title: 'Investors',
    body: 'We are open to discussions with crypto-native funds, angels, and strategic LPs. Pre-seed and seed-stage conversations welcomed.',
    email: 'investors@haythix.com',
    eyebrow: 'Funds + angels',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        Include your fund / personal background, check size, and stage you typically lead. We will reply with a deck and basic metrics if there is fit.
      </p>
    ),
  },
  {
    key: 'press',
    icon: Newspaper,
    accent: 'blue',
    title: 'Press + media',
    body: 'Interview requests, podcast bookings, quotes for articles, journalist questions about the crypto trading or DeFi space.',
    email: 'press@haythix.com',
    eyebrow: 'Journalists + podcasters',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        Include your outlet, deadline, and the angle you are exploring. Background materials available on request.
      </p>
    ),
  },
  {
    key: 'bugs',
    icon: Bug,
    accent: 'red',
    title: 'Security + bugs',
    body: 'Vulnerability reports, suspicious behaviour, account hijack concerns. Triaged immediately during business hours.',
    email: 'security@haythix.com',
    eyebrow: 'Critical issues',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        For urgent in-flight issues affecting funds or account integrity, also message{' '}
        <a href="https://t.me/Haythix-Official" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">@Haythix-Official</a>{' '}
        on Telegram so the team can respond in real time.
      </p>
    ),
  },
  {
    key: 'advertise',
    icon: BarChart3,
    accent: 'cyan',
    title: 'Advertising',
    body: 'Sponsor banners on haythix, promote a token / protocol / tool to active traders. Self-serve order form available.',
    email: 'info@haythix.com',
    eyebrow: 'Sponsors',
    extra: (
      <p className="text-gray-500 text-xs mt-3">
        Skip the email and configure your placement directly on the{' '}
        <Link to="/advertise" className="text-amber-400 hover:underline">Advertise page</Link>.
      </p>
    ),
  },
];

const ACCENT_STYLES = {
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400',   pill: 'bg-amber-500/15 text-amber-300' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', pill: 'bg-emerald-500/15 text-emerald-300' },
  violet:  { bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  text: 'text-violet-400',  pill: 'bg-violet-500/15 text-violet-300' },
  blue:    { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-400',    pill: 'bg-blue-500/15 text-blue-300' },
  red:     { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-400',     pill: 'bg-red-500/15 text-red-300' },
  cyan:    { bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30',    text: 'text-cyan-400',    pill: 'bg-cyan-500/15 text-cyan-300' },
};

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-dark-900 text-gray-200">
      <Seo
        title="Contact haythix — Support, Partnerships, Investors, Press"
        description="Reach haythix for customer support, partnership opportunities, investor inquiries, press requests, or security issues. Real humans, business-hour response."
        canonical="/contact"
      />
      <PublicNav />

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold uppercase tracking-wider mb-4">
            <Mail className="w-3.5 h-3.5" /> Contact
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            How to reach haythix
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Real humans answer every email. Pick the right inbox below so your message lands in front of the person who can actually help.
          </p>
        </div>

        {/* Channel cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            const style = ACCENT_STYLES[c.accent] || ACCENT_STYLES.amber;
            return (
              <div key={c.key} className={`rounded-2xl border ${style.border} ${style.bg} p-5 flex flex-col`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl ${style.bg} ${style.border} border flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${style.pill} mb-1`}>
                      {c.eyebrow}
                    </span>
                    <h3 className="text-white font-bold text-base">{c.title}</h3>
                  </div>
                </div>
                <p className="text-gray-300 text-sm flex-1 leading-relaxed">{c.body}</p>
                <a
                  href={`mailto:${c.email}`}
                  className={`mt-4 flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-dark-800 border ${style.border} hover:bg-dark-700 transition-colors group`}
                >
                  <span className={`font-mono text-sm ${style.text}`}>{c.email}</span>
                  <ArrowRight className={`w-4 h-4 ${style.text} group-hover:translate-x-1 transition-transform`} />
                </a>
                {c.extra}
              </div>
            );
          })}
        </div>

        {/* Community channels */}
        <div className="rounded-2xl border border-dark-700/50 bg-dark-800 p-8 mb-12">
          <h2 className="text-white font-bold text-xl mb-1 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-amber-400" />
            Community + social
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Public channels where the haythix team posts updates and engages with the community. Faster than email for general questions.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            <a
              href="https://t.me/haythix"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-xl bg-dark-700/50 border border-dark-600/50 hover:border-amber-500/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm">Telegram channel</p>
                <p className="text-gray-500 text-xs mb-1">Announcements, updates, market commentary</p>
                <p className="text-amber-400 font-mono text-xs">@haythix</p>
              </div>
            </a>

            <a
              href="https://t.me/Haythix-Official"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-xl bg-dark-700/50 border border-dark-600/50 hover:border-amber-500/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-violet-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm">Telegram community</p>
                <p className="text-gray-500 text-xs mb-1">Discussion, bot tips, questions answered</p>
                <p className="text-amber-400 font-mono text-xs">@Haythix-Official</p>
              </div>
            </a>

            <a
              href="https://x.com/haythix"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-xl bg-dark-700/50 border border-dark-600/50 hover:border-amber-500/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-700 border border-gray-600 flex items-center justify-center flex-shrink-0">
                <Twitter className="w-5 h-5 text-gray-300" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm">X (Twitter)</p>
                <p className="text-gray-500 text-xs mb-1">Daily posts, contrarian takes, market threads</p>
                <p className="text-amber-400 font-mono text-xs">@haythix</p>
              </div>
            </a>
          </div>
        </div>

        {/* Response time + entity */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-dark-700/50 bg-dark-800 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <h3 className="text-white font-bold">Response times</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>→ <span className="text-white">Customer support:</span> within 1 business day</li>
              <li>→ <span className="text-white">Partnerships:</span> within 2-3 business days</li>
              <li>→ <span className="text-white">Investors:</span> within 2 business days if there is fit</li>
              <li>→ <span className="text-white">Press:</span> within 1 business day</li>
              <li>→ <span className="text-white">Security:</span> same business day; for funds-at-risk reach us on Telegram first</li>
            </ul>
            <p className="text-gray-500 text-xs mt-4">
              Crypto markets are 24/7; the team is not. Business hours are Monday-Friday, US Eastern.
            </p>
          </div>

          <div className="rounded-2xl border border-dark-700/50 bg-dark-800 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-amber-400" />
              <h3 className="text-white font-bold">About haythix</h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">
              haythix is an AI-native multi-chain crypto trading platform — unifying centralized exchange trading, on-chain DEX execution, automated bots, copy trading, and tax-grade reporting in one product.
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>No fake track records · No mock data · Verifiable-methodology positioning</span>
            </div>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
